/**
 * Mistral AI provider adapter for ludicrous-gateway
 * Connects to Mistral AI's OpenAI-compatible API
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig, ApiKeyConfig } from "../types/config.js";
import type { TextContent } from "../types/messages.js";

/**
 * Available Mistral models
 */
export const MISTRAL_MODELS = [
  { value: "mistral-large-latest", label: "Mistral Large (Best)" },
  { value: "mistral-medium-latest", label: "Mistral Medium" },
  { value: "mistral-small-latest", label: "Mistral Small (Fast)" },
  { value: "codestral-latest", label: "Codestral (Code-focused)" },
  { value: "ministral-8b-latest", label: "Ministral 8B (Cheapest)" },
] as const;

/**
 * Mistral OpenAI-compatible request format
 */
interface MistralRequest {
  model: string;
  messages: Array<{
    role: "system" | "user" | "assistant";
    content: string;
  }>;
  stream: boolean;
  temperature?: number | undefined;
  top_p?: number | undefined;
  max_tokens?: number | undefined;
  stop?: string[] | undefined;
}

/**
 * Mistral response format (OpenAI-compatible)
 */
interface MistralResponse {
  id: string;
  object: "chat.completion";
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: "assistant";
      content: string;
    };
    finish_reason: "stop" | "length" | "tool_calls" | null;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * Mistral streaming chunk format
 */
interface MistralStreamChunk {
  id: string;
  object: "chat.completion.chunk";
  created: number;
  model: string;
  choices: Array<{
    index: number;
    delta: {
      role?: "assistant";
      content?: string;
    };
    finish_reason: "stop" | "length" | "tool_calls" | null;
  }>;
}

/**
 * Default model aliases mapping Claude models to Mistral
 */
export const DEFAULT_MISTRAL_ALIASES: Record<string, string> = {
  "claude-opus-4-5-20251101": "mistral-large-latest",
  "claude-sonnet-4-20251114": "mistral-medium-latest",
  "claude-3-5-haiku-20241022": "mistral-small-latest",
};

/**
 * Mistral AI provider
 */
export class MistralProvider extends BaseProvider {
  private apiKeys: ApiKeyConfig[];
  private currentKeyIndex: number = 0;

  constructor(config: ProviderConfig) {
    super(config);
    this.apiKeys = config.keys ?? [];

    // Apply default aliases if none configured
    if (!config.model_aliases || Object.keys(config.model_aliases).length === 0) {
      for (const [from, to] of Object.entries(DEFAULT_MISTRAL_ALIASES)) {
        this.modelAliases.set(from, to);
      }
    }

    if (this.apiKeys.length === 0) {
      this.logger.warn("No API keys configured for Mistral provider");
    }
  }

  protected getDefaultBaseUrl(): string {
    return "https://api.mistral.ai";
  }

  /**
   * Get the next API key (round-robin)
   */
  private getNextApiKey(): string | null {
    if (this.apiKeys.length === 0) {
      return null;
    }

    const key = this.apiKeys[this.currentKeyIndex];
    this.currentKeyIndex = (this.currentKeyIndex + 1) % this.apiKeys.length;
    return key?.key ?? null;
  }

  /**
   * Build headers for Mistral API
   */
  protected override buildHeaders(
    _context: ProviderRequestContext
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    const apiKey = this.getNextApiKey();
    if (apiKey) {
      headers["Authorization"] = `Bearer ${apiKey}`;
    }

    return headers;
  }

  /**
   * Convert Anthropic messages to Mistral format
   */
  private convertToMistralFormat(
    request: MessagesRequest,
    resolvedModel: string
  ): MistralRequest {
    const messages: MistralRequest["messages"] = [];

    // Add system prompt if present
    if (request.system) {
      const systemText =
        typeof request.system === "string"
          ? request.system
          : request.system
              .filter((b): b is TextContent => b.type === "text")
              .map((b) => b.text)
              .join("\n");

      messages.push({
        role: "system",
        content: systemText,
      });
    }

    // Convert messages
    for (const msg of request.messages) {
      let content: string;

      if (typeof msg.content === "string") {
        content = msg.content;
      } else {
        content = msg.content
          .filter((b): b is TextContent => b.type === "text")
          .map((b) => b.text)
          .join("\n");
      }

      messages.push({
        role: msg.role,
        content,
      });
    }

    return {
      model: resolvedModel,
      messages,
      stream: request.stream ?? false,
      temperature: request.temperature,
      top_p: request.top_p,
      max_tokens: request.max_tokens,
      stop: request.stop_sequences,
    };
  }

  /**
   * Convert Mistral response to Anthropic format
   */
  private convertFromMistralFormat(
    response: MistralResponse,
    model: string
  ): MessagesResponse {
    const choice = response.choices[0];

    return {
      id: response.id,
      type: "message",
      role: "assistant",
      content: [
        {
          type: "text",
          text: choice?.message?.content ?? "",
        },
      ],
      model,
      stop_reason:
        choice?.finish_reason === "stop"
          ? "end_turn"
          : choice?.finish_reason === "length"
            ? "max_tokens"
            : "end_turn",
      stop_sequence: null,
      usage: {
        input_tokens: response.usage.prompt_tokens,
        output_tokens: response.usage.completion_tokens,
      },
    };
  }

  /**
   * Send a non-streaming request to Mistral
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/chat/completions`;
      const headers = this.buildHeaders(context);
      const mistralRequest = this.convertToMistralFormat(
        request,
        context.resolvedModel
      );
      mistralRequest.stream = false;

      this.logger.debug("Sending request to Mistral", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(mistralRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Mistral API error: ${response.status} - ${errorText}`);
      }

      const data = (await response.json()) as MistralResponse;
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("Mistral request successful", { latencyMs });

      return this.convertFromMistralFormat(data, context.resolvedModel);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("Mistral request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to Mistral
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/chat/completions`;
      const headers = this.buildHeaders(context);
      headers["Accept"] = "text/event-stream";

      const mistralRequest = this.convertToMistralFormat(
        request,
        context.resolvedModel
      );
      mistralRequest.stream = true;

      this.logger.debug("Starting streaming request to Mistral", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(mistralRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Mistral API error: ${response.status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Send initial message_start event (Anthropic SSE format)
      handler.onEvent(
        "message_start",
        JSON.stringify({
          type: "message_start",
          message: {
            id: `mistral-${Date.now()}`,
            type: "message",
            role: "assistant",
            content: [],
            model: context.resolvedModel,
            stop_reason: null,
            stop_sequence: null,
            usage: { input_tokens: 0, output_tokens: 0 },
          },
        })
      );

      // Send content_block_start
      handler.onEvent(
        "content_block_start",
        JSON.stringify({
          type: "content_block_start",
          index: 0,
          content_block: { type: "text", text: "" },
        })
      );

      // Process SSE stream from Mistral
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;

          const data = line.slice(6);
          if (data === "[DONE]") {
            // Send final events
            handler.onEvent(
              "content_block_stop",
              JSON.stringify({
                type: "content_block_stop",
                index: 0,
              })
            );

            handler.onEvent(
              "message_delta",
              JSON.stringify({
                type: "message_delta",
                delta: {
                  stop_reason: "end_turn",
                  stop_sequence: null,
                },
                usage: {
                  output_tokens: 0, // Mistral doesn't provide in streaming
                },
              })
            );

            handler.onEvent(
              "message_stop",
              JSON.stringify({
                type: "message_stop",
              })
            );
            continue;
          }

          try {
            const chunk = JSON.parse(data) as MistralStreamChunk;
            const content = chunk.choices[0]?.delta?.content;

            if (content) {
              handler.onEvent(
                "content_block_delta",
                JSON.stringify({
                  type: "content_block_delta",
                  index: 0,
                  delta: {
                    type: "text_delta",
                    text: content,
                  },
                })
              );
            }
          } catch {
            // Skip malformed chunks
            this.logger.debug("Skipping malformed Mistral response chunk");
          }
        }
      }

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("Mistral streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("Mistral streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }

  /**
   * Health check for Mistral
   */
  override async healthCheck(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      // Mistral models endpoint to check health
      const response = await fetch(`${this.baseUrl}/v1/models`, {
        method: "GET",
        headers: this.buildHeaders({} as ProviderRequestContext),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      this.recordHealthCheck(response.ok);
      return response.ok;
    } catch (error) {
      this.recordHealthCheck(
        false,
        error instanceof Error ? error.message : "Unknown error"
      );
      return false;
    }
  }
}

/**
 * Create a Mistral provider from config
 */
export function createMistralProvider(config: ProviderConfig): MistralProvider {
  return new MistralProvider(config);
}
